import { AudienceRecommendationRecommendationItem, TaxonomyApi, CountryItem, AudiencePlatformItem } from '@digiseg-labs/api-client';
import { Box, Flex, Heading } from '@radix-ui/themes';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTaxonomy } from '../hooks/useTaxonomy';
import ChatInput from './ChatInput';
import ConversationList, { ConversationItem } from './ConversationList';

export const PromptApp = () => {
    const [searchParams] = useSearchParams();
    const [country, setCountry] = useState<CountryItem | undefined>();
    const [platform, setPlatform] = useState<AudiencePlatformItem | undefined>();
    const [inputValue, setInputValue] = useState('');
    const [conversation, setConversation] = useState<ConversationItem[]>([]);
    const [conversationId, setConversationId] = useState<string | null>(null);
    const [showHeaders, setShowHeaders] = useState(true);
    const [shouldRenderHeaders, setShouldRenderHeaders] = useState(true);
    const [headerAnimation, setHeaderAnimation] = useState<'fadeInDown' | 'fadeOutUp'>('fadeInDown');
    const [hasSubmittedOnce, setHasSubmittedOnce] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { countries, platforms } = useTaxonomy();

    const footerObserverRef = useRef<IntersectionObserver | null>(null);
    const chatViewportRef = useRef<HTMLDivElement | null>(null);
    const urlPrompt = useRef<string | null>(searchParams.get('prompt'));
    const handleSubmitRef = useRef<() => void>(() => { });
    const [isAtFooter, setIsAtFooter] = useState(false);

    const handleSubmit = useCallback(async () => {
        if (!inputValue.trim() || isSubmitting) return;
        const trimmed = inputValue.trim();
        setInputValue('');
        setIsSubmitting(true);
        setShowHeaders(false);
        if (!hasSubmittedOnce) setHasSubmittedOnce(true);
        setConversation(prev => [...prev, { sender: 'user', message: trimmed }, { sender: 'ai', message: '', isLoading: true }]);
        try {
            const api = new TaxonomyApi();
            const res = await api.completeAudienceRecommendation({ message_text: trimmed, conversation_id: conversationId ?? undefined });
            if (res.data.data?.conversation_id && !conversationId) setConversationId(res.data.data.conversation_id);
            const msg = res.data.data?.message_text || '';
            const recs: AudienceRecommendationRecommendationItem[] = res.data.data?.recommendations || [];
            setConversation(prev => {
                const next = [...prev];
                const idx = next.findIndex(m => m.sender === 'ai' && m.isLoading);
                if (idx !== -1) next[idx] = { sender: 'ai', message: msg, recommendations: recs, isLoading: false };
                return next;
            });
        } catch (e) {
            console.error(e);
            setConversation(prev => {
                const next = [...prev];
                const idx = next.findIndex(m => m.sender === 'ai' && m.isLoading);
                if (idx !== -1) next[idx] = { sender: 'ai', message: 'Sorry, something went wrong.', recommendations: [], isLoading: false };
                return next;
            });
        } finally {
            setIsSubmitting(false);
        }
    }, [inputValue, isSubmitting, conversationId, hasSubmittedOnce]);


    useEffect(() => {
        handleSubmitRef.current = handleSubmit;
    }, [handleSubmit]);

    useEffect(() => {
        if (urlPrompt.current && !hasSubmittedOnce && !isSubmitting) {
            setInputValue(urlPrompt.current);
            setTimeout(() => handleSubmitRef.current(), 50);   // tiny delay, no lodash needed
            urlPrompt.current = null;
        }
    }, [hasSubmittedOnce, isSubmitting]);

    useEffect(() => {
        const footer = document.querySelector('footer');
        if (!footer) return;
        footerObserverRef.current = new IntersectionObserver(([entry]) => setIsAtFooter(entry.isIntersecting), { root: null, threshold: 0 });
        footerObserverRef.current.observe(footer);
        return () => footerObserverRef.current?.disconnect();
    }, []);

    useEffect(() => {
        if (!showHeaders) {
            setHeaderAnimation('fadeOutUp');
            const t = setTimeout(() => setShouldRenderHeaders(false), 600);
            return () => clearTimeout(t);
        }
    }, [showHeaders]);

    useEffect(() => {
        if (!hasSubmittedOnce || !conversation.length) return;
        const last = conversation[conversation.length - 1];
        if (last.sender !== 'ai') return;
        requestAnimationFrame(() => {
            const el = chatViewportRef.current;
            if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
        });
    }, [conversation, hasSubmittedOnce]);

    return (
        <Box className="App-wrapper">
            {!hasSubmittedOnce && (
                <Flex align="center" className="intro-container">
                    <Box className="intro-box">
                        {shouldRenderHeaders && (
                            <div className={headerAnimation}>
                                <Heading as="h1" size="7" mb="2" align="center">What product or service are you advertising?</Heading>
                                <Heading as="h2" size="5" weight="regular" align="center">Find the right audience that suits your needs with our AI.</Heading>
                            </div>
                        )}
                        <ConversationList conversation={conversation} countries={countries} platforms={platforms} selectedCountry={country} selectedPlatform={platform} onCountrySelected={setCountry} onPlatformSelected={setPlatform} />
                        <ChatInput inputValue={inputValue} onInputValueChange={setInputValue} onSubmit={handleSubmit} />
                    </Box>
                </Flex>
            )}
            {hasSubmittedOnce && (
                <>
                    <Box className="chat-box-scroll" ref={chatViewportRef}>
                        <Flex className="chat-content" direction="column">
                            <ConversationList conversation={conversation} countries={countries} platforms={platforms} selectedCountry={country} selectedPlatform={platform} onCountrySelected={setCountry} onPlatformSelected={setPlatform} />
                        </Flex>
                    </Box>
                    <Box className={`chat-box-input ${isAtFooter ? 'chat-box-stopped' : ''}`}>
                        <Box className="chat-input-inner" m="auto">
                            <ChatInput inputValue={inputValue} onInputValueChange={setInputValue} onSubmit={handleSubmit} isDisabled={isSubmitting} isLoading={isSubmitting} />
                        </Box>
                    </Box>
                </>
            )}
        </Box>
    );
};
